import { useCallback, useMemo, useRef, useState } from "react";
import PageShell, { RefreshButton } from "../components/PageShell";
import AddGoalDialog from "../components/AddGoalDialog";
import EmptyState from "../components/EmptyState";
import ErrorState from "../components/ErrorState";
import GoalRow from "../components/GoalRow";
import { Skeleton } from "../components/LoadingSkeleton";
import MetaBadge from "../components/MetaBadge";
import PeriodSwitcher, {
  daysLeftIn,
  periodLabel,
  weekNumberHint,
} from "../components/PeriodSwitcher";
import ProgressBar from "../components/ProgressBar";
import TodoPanel from "../components/TodoPanel";
import { describeError, useApi } from "../hooks/useApi";
import { usePageRefresh } from "../hooks/usePageRefresh";
import { useOptimisticWrites } from "../hooks/usePendingWrites";
import { useRemembered } from "../hooks/useRemembered";
import { useToast } from "../hooks/useToast";
import {
  addGoalStep,
  createGoal,
  deleteGoal,
  deleteGoalStep,
  getGoalPeriods,
  getGoals,
  logGoalProgress,
  patchGoal,
  patchGoalStep,
} from "../lib/api";
import {
  previewPatch,
  previewProgress,
  previewStep,
  previewStepRemoved,
} from "../lib/goals";
import { retireSettled } from "../lib/pending";
import { pct } from "../lib/format";
import type {
  GoalCreate,
  GoalLevel,
  GoalNode,
  GoalPatch,
  GoalPeriods,
  GoalsOverview,
} from "../types";

type StatusFilter = "all" | "active" | "done";

/**
 * One unconfirmed edit of one goal: the fields it changes on screen, or that
 * the row should not be there at all. See `hooks/usePendingWrites`.
 */
type GoalEdit = { goalId: string; patch?: Partial<GoalNode>; gone?: boolean };

/** Order matters: this is the order of the chips, and the first one is default. */
const STATUS_UA: Record<StatusFilter, string> = {
  all: "Всі",
  active: "Активні",
  done: "Закриті",
};

/**
 * Goals: one period at a time, with today's tasks beside it.
 *
 * The period switcher is the whole navigation model — there is no year/month/
 * week tree, because goals carry no parent link. Levels are three independent
 * ladders and the page never pretends otherwise.
 */
export default function GoalsPage() {
  // Everything with a control that changes what the list means is remembered
  // until the end of the day.
  const [level, setLevel] = useRemembered<GoalLevel>("jv.goals.level", "week");
  const [pinned, setPinned] = useRemembered<Partial<Record<GoalLevel, string>>>(
    "jv.goals.period",
    {},
  );
  // "Всі" by default: ticking a goal must not make it vanish from under the
  // cursor, because the only way back is the checkbox that just disappeared.
  const [status, setStatus] = useRemembered<StatusFilter>("jv.goals.status", "all");
  const [byProgress, setByProgress] = useRemembered("jv.goals.sort", false);
  const [adding, setAdding] = useState(false);

  const pinnedKey = pinned[level];
  const fetchGoals = useCallback(
    () => getGoals(pinnedKey ? { [level]: pinnedKey } : undefined),
    [level, pinnedKey],
  );

  const goals = useApi<GoalsOverview>(fetchGoals, [fetchGoals], { pollMs: 300_000 });
  const periods = useApi<GoalPeriods>(getGoalPeriods, []);
  // Goals are local; the Todoist panel beside them is what «Оновити» re-asks.
  const { refresh, busy } = usePageRefresh([goals, periods]);
  const data = goals.data;

  const activeKey = data
    ? { year: data.current_year_key, month: data.current_month_key, week: data.current_week_key }[
        level
      ]
    : (pinnedKey ?? "");

  /**
   * Edits show on the row before the server has agreed to them.
   *
   * Every write here used to freeze the whole page — one `busy` flag disabling
   * every checkbox, field and button on it — and then leave the row reading its
   * old value until a refetch came back. From a phone on mobile data that is
   * a visible pause on the most-used control in the app, for a write that goes
   * to the database. Now the row moves on the click; failures come back
   * as a toast with «Повторити» (`hooks/usePendingWrites`).
   */
  const { writes, run } = useOptimisticWrites<GoalEdit>(goals.startedAt, goals.refetch);
  const editSeq = useRef(1);
  const toast = useToast();
  const refetchPeriods = periods.refetch;

  /** One optimistic edit of one goal: how it reads now, and what is sent. */
  const editGoal = useCallback(
    (goal: GoalNode, patch: GoalPatch, preview: Partial<GoalNode>) => {
      void run({
        // A fresh key per edit rather than the goal id: two fields committed
        // in quick succession are two edits, and keying them alike would drop
        // the first one's preview the moment the second began.
        key: `${goal.id}|${editSeq.current++}`,
        edit: { goalId: goal.id, patch: preview },
        send: () => patchGoal(goal.id, patch),
        failure: `Не вдалося зберегти «${goal.title}».`,
        retry: () => editGoal(goal, patch, preview),
      });
    },
    [run],
  );

  const removeGoal = useCallback(
    (goal: GoalNode) => {
      void run({
        key: `${goal.id}|${editSeq.current++}`,
        edit: { goalId: goal.id, gone: true },
        send: () => deleteGoal(goal.id),
        failure: `Не вдалося видалити «${goal.title}».`,
        retry: () => removeGoal(goal),
        // Deleting the last goal of a period retires the period itself.
      }).then((ok) => ok && refetchPeriods());
    },
    [run, refetchPeriods],
  );

  const logProgress = useCallback(
    (goal: GoalNode, value: number) => {
      void run({
        key: `${goal.id}|${editSeq.current++}`,
        edit: { goalId: goal.id, patch: previewProgress(goal, value) },
        send: () => logGoalProgress(goal.id, { value }),
        failure: `Не вдалося записати прогрес «${goal.title}».`,
        retry: () => logProgress(goal, value),
      });
    },
    [run],
  );

  const addStep = useCallback(
    (goal: GoalNode, text: string) => {
      void run({
        key: `${goal.id}|${editSeq.current++}`,
        // No preview: a step the server has not created yet has no id to tick,
        // and unlike a Todoist write this is one local INSERT away.
        edit: { goalId: goal.id },
        send: () => addGoalStep(goal.id, text),
        failure: `Не вдалося додати пункт до «${goal.title}».`,
        retry: () => addStep(goal, text),
      });
    },
    [run],
  );

  const toggleStep = useCallback(
    (goal: GoalNode, stepId: string, done: boolean) => {
      void run({
        key: `${goal.id}|${editSeq.current++}`,
        edit: { goalId: goal.id, patch: previewStep(goal, stepId, done) },
        send: () => patchGoalStep(stepId, { done }),
        failure: `Не вдалося зберегти пункт «${goal.title}».`,
        retry: () => toggleStep(goal, stepId, done),
      });
    },
    [run],
  );

  const removeStep = useCallback(
    (goal: GoalNode, stepId: string) => {
      void run({
        key: `${goal.id}|${editSeq.current++}`,
        edit: { goalId: goal.id, patch: previewStepRemoved(goal, stepId) },
        send: () => deleteGoalStep(stepId),
        failure: `Не вдалося видалити пункт «${goal.title}».`,
        retry: () => removeStep(goal, stepId),
      });
    },
    [run],
  );

  /**
   * The level's goals with every unconfirmed edit already applied.
   *
   * Retired at render rather than only in the effect behind it, so an edit the
   * data on screen already contains stops counting in the same frame it
   * arrives — see `lib/pending.ts`.
   */
  const levelGoals = useMemo(() => {
    const live = retireSettled(writes, goals.startedAt);
    const rows = data?.[level] ?? [];
    if (live.length === 0) return rows;

    const gone = new Set<string>();
    const patches = new Map<string, Partial<GoalNode>>();
    for (const write of live) {
      if (write.value.gone) gone.add(write.value.goalId);
      else if (write.value.patch) {
        // Folded in issue order, so the later of two edits to one field wins.
        patches.set(write.value.goalId, {
          ...patches.get(write.value.goalId),
          ...write.value.patch,
        });
      }
    }
    return rows
      .filter((goal) => !gone.has(goal.id))
      .map((goal) => {
        const patch = patches.get(goal.id);
        return patch ? { ...goal, ...patch } : goal;
      });
  }, [data, level, writes, goals.startedAt]);

  const visible = useMemo(() => {
    const all = levelGoals;
    const filtered = all.filter((goal) => {
      if (status === "active") return !goal.done && goal.status !== "dropped";
      // A dropped goal is closed too — without this it belonged to neither
      // filter and could only be found under «Всі».
      if (status === "done") return goal.done || goal.status === "dropped";
      return true;
    });
    return byProgress
      ? [...filtered].sort((a, b) => a.progress_pct - b.progress_pct)
      : filtered;
  }, [levelGoals, status, byProgress]);

  const daysLeft = activeKey ? daysLeftIn(level, activeKey) : null;
  // Dropped goals are out of every count on this page: an abandoned goal is not
  // one you are failing at, and averaging its 0% in dragged the period down for
  // as long as the row stayed on the list.
  const counted = useMemo(
    () => levelGoals.filter((goal) => goal.status !== "dropped"),
    [levelGoals],
  );
  const doneCount = counted.filter((goal) => goal.done).length;
  const levelProgress = counted.length
    ? Math.round(counted.reduce((sum, g) => sum + g.progress_pct, 0) / counted.length)
    : 0;

  /**
   * Creating is the one write here that is *not* optimistic: a goal the server
   * has not made yet has no id, no position and no created_at, and inventing
   * them to save a round trip on something done twice a week is a bad trade.
   *
   * The dialog closes on submit anyway. It used to stay open until the write
   * landed so a rejected goal could be fixed rather than retyped — the retry in
   * the toast carries the whole payload, so nothing is retyped either way, and
   * the common case stops paying for the rare one.
   */
  const addGoal = useCallback(
    async (payload: GoalCreate) => {
      try {
        await createGoal(payload);
        goals.refetch();
        // A goal in a period nothing has been filed under yet creates it.
        refetchPeriods();
      } catch (err) {
        toast({
          message: `Не вдалося створити «${payload.title}».`,
          detail: describeError(err).message,
          key: "goal:create",
          action: { label: "Повторити", onSelect: () => void addGoal(payload) },
        });
      }
    },
    [goals, refetchPeriods, toast],
  );

  return (
    <PageShell
      title="Цілі"
      actions={
        <div className="jv-toolbar flex items-center gap-2">
          <div role="radiogroup" aria-label="Фільтр" className="flex gap-1">
            {(Object.keys(STATUS_UA) as StatusFilter[]).map((key) => (
              <button
                key={key}
                type="button"
                role="radio"
                aria-checked={status === key}
                onClick={() => setStatus(key)}
                className={`jv-btn ${status === key ? "border-accent text-accent" : ""}`}
              >
                {STATUS_UA[key]}
              </button>
            ))}
          </div>
          <button
            type="button"
            onClick={() => setByProgress(!byProgress)}
            aria-pressed={byProgress}
            title="Сортувати за прогресом"
            className={`jv-btn ${byProgress ? "border-accent text-accent" : ""}`}
          >
            ↑ Прогрес
          </button>
          <RefreshButton onClick={refresh} busy={busy} />
        </div>
      }
      meta={<MetaBadge meta={data?.meta} fetchedAt={goals.fetchedAt} />}
    >
      {/* Shaped like the loaded page — tasks rail on the right, period switcher
          + goal list on the left — not a stat grid this page never shows. The
          card that used to sit above the switcher went with the pace verdict
          (2026-09-20); design-system §6 says the skeleton follows. */}
      {goals.loading && !data && (
        <div
          role="status"
          aria-busy="true"
          aria-label="Завантаження"
          className="flex flex-col gap-3 sm:gap-4 lg:grid lg:grid-cols-[minmax(0,1fr)_19rem]"
        >
          <div className="jv-card space-y-2.5 p-3 sm:p-4 lg:col-start-2 lg:row-start-1">
            <Skeleton className="h-4 w-24" />
            <Skeleton className="h-4 w-11/12" />
            <Skeleton className="h-4 w-9/12" />
            <Skeleton className="h-4 w-10/12" />
          </div>
          <div className="flex min-w-0 flex-col gap-3 sm:gap-4 lg:col-start-1 lg:row-start-1">
            <Skeleton className="h-8 w-64 max-w-full" />
            <div className="jv-card space-y-3 p-3 sm:p-4">
              {/* Period line, the period's own bar, then two goal rows. */}
              <Skeleton className="h-4 w-1/2" />
              <Skeleton className="h-1.5 w-full" />
              <Skeleton className="h-4 w-2/3" />
              <Skeleton className="h-1.5 w-full" />
              <Skeleton className="h-4 w-2/3" />
              <Skeleton className="h-1.5 w-full" />
            </div>
          </div>
        </div>
      )}

      {!data && goals.error && (
        <div className="jv-card">
          <ErrorState
            title="Цілі не завантажились"
            message={goals.error}
            code={goals.code}
            onRetry={goals.refetch}
          />
        </div>
      )}

      {data && (
        <div className="flex flex-col gap-3 sm:gap-4 lg:grid lg:grid-cols-[minmax(0,1fr)_19rem]">
          <TodoPanel className="lg:col-start-2 lg:row-start-1" />

          <div className="flex min-w-0 flex-col gap-3 sm:gap-4 lg:col-start-1 lg:row-start-1">
            <PeriodSwitcher
              level={level}
              onLevel={setLevel}
              periods={periods.data?.[level] ?? [activeKey]}
              value={activeKey}
              onValue={(key) => setPinned({ ...pinned, [level]: key })}
              currentKey={
                { year: data.current_year_key, month: data.current_month_key, week: data.current_week_key }[
                  level
                ]
              }
            />

            {/* The period's own header: what it is, how long is left, and how
                far the goals under it have got. There used to be a card above
                this one announcing which goals were behind the clock — that
                pace verdict is gone, and with it the only thing that card
                said. What is left are the goals' own numbers. */}
            <section className="jv-card p-3 sm:p-4">
              <div className="mb-2.5 flex flex-wrap items-center gap-x-3 gap-y-1">
                <h2
                  title={weekNumberHint(level, activeKey)}
                  className="shrink-0 text-sm font-semibold text-fg"
                >
                  {periodLabel(level, activeKey)}
                </h2>
                {daysLeft !== null && (
                  <span className="shrink-0 text-xs text-muted">
                    {daysLeft === 0 ? "останній день" : `лишилось ${daysLeft} дн.`}
                  </span>
                )}
                {counted.length > 0 && (
                  <span className="ml-auto shrink-0 text-xs text-muted">
                    <span className="jv-num">{doneCount}</span> з{" "}
                    <span className="jv-num">{counted.length}</span> закрито
                  </span>
                )}
              </div>

              {counted.length > 0 && (
                <div className="mb-3 flex items-center gap-2">
                  <ProgressBar
                    value={levelProgress}
                    tone={levelProgress >= 100 ? "ok" : "accent"}
                    height={6}
                    className="flex-1"
                  />
                  <span className="jv-num shrink-0 text-xs text-muted">
                    {pct(levelProgress, 0)}
                  </span>
                </div>
              )}

              {visible.length === 0 ? (
                <EmptyState
                  compact
                  title={levelGoals.length ? "Нічого не знайшлось" : "Цілей ще немає"}
                />
              ) : (
                <ul className="flex flex-col">
                  {visible.map((goal) => (
                    <GoalRow
                      key={goal.id}
                      goal={goal}
                      onEdit={(g: GoalNode, patch: GoalPatch) =>
                        editGoal(g, patch, previewPatch(g, patch))
                      }
                      onDelete={removeGoal}
                      onLogProgress={logProgress}
                      onAddStep={addStep}
                      onToggleStep={toggleStep}
                      onDeleteStep={removeStep}
                    />
                  ))}
                </ul>
              )}

              <button
                type="button"
                onClick={() => setAdding(true)}
                disabled={!activeKey}
                className="mt-3 w-full rounded-control border border-dashed border-line py-2 text-sm text-muted transition-colors hover:border-accent hover:text-accent disabled:opacity-50"
              >
                + Ціль
              </button>
            </section>
          </div>
        </div>
      )}

      {activeKey && (
        <AddGoalDialog
          open={adding}
          onClose={() => setAdding(false)}
          onCreate={(payload) => {
            // Closed on submit, not on the response: the dialog has taken
            // everything it needs, and a failure comes back as a toast whose
            // «Повторити» still carries the payload.
            setAdding(false);
            void addGoal(payload);
          }}
          level={level}
          periodKey={activeKey}
          periodText={periodLabel(level, activeKey)}
        />
      )}
    </PageShell>
  );
}
